// src/features/vita/deeplink/useDeepLinks.js
//
// Escuta deep links no app NATIVO.
//
// No navegador, o link já chega como navegação normal e a rota /app/:destino
// resolve sozinha. No app instalado é diferente: o sistema entrega a URL ao
// processo em vez de navegar, e sem este hook o app abre na home e o clique no
// link vindo do WhatsApp se perde — que é justamente o handoff que o Bloco I
// existe para fazer funcionar.
//
// Dois momentos, os dois necessários:
//   • app JÁ ABERTO → evento `appUrlOpen` (vem de `onNewIntent`)
//   • app FECHADO   → `App.getLaunchUrl()` (a URL veio no intent INICIAL, e com
//                     `launchMode="singleTask"` o `onNewIntent` não roda)
//
// ORDEM IMPORTA: o listener é registrado ANTES de consultar a launch URL. Ao
// contrário, um `appUrlOpen` que chegasse durante o `await` seria perdido.
//
// A lógica de decisão (parse, rota, deduplicação, fallback) mora em
// `deepLinkHandler` — os dois caminhos chamam a MESMA função, e ela é testada
// sem React.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

import { EVENTO_ABRIR_CHAT } from "./deepLinkRoutes";
import {
  ORIGEM_COLD_START,
  ORIGEM_LISTENER,
  criarTratadorDeepLink,
} from "./deepLinkHandler";

export default function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let remover;
    let cancelado = false;

    const tratar = criarTratadorDeepLink({
      navegar: (rota) => navigate(rota),
      abrirChat: () => window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_CHAT)),
    });

    (async () => {
      try {
        const { App } = await import("@capacitor/app");

        // 1) Listener primeiro — nada que chegue daqui pra frente se perde.
        const handle = await App.addListener("appUrlOpen", (evento) => {
          if (!cancelado) tratar(evento?.url, ORIGEM_LISTENER);
        });
        remover = () => handle.remove();
        if (cancelado) {
          remover();
          return;
        }

        // 2) Cold start: o app foi ABERTO pelo link. Se o listener já tiver
        //    tratado a mesma URL (acontece no iOS), a guarda de duplicata do
        //    tratador ignora esta chamada — sem navegação dupla.
        try {
          const lancamento = await App.getLaunchUrl();
          if (!cancelado && lancamento?.url) {
            tratar(lancamento.url, ORIGEM_COLD_START);
          }
        } catch {
          /* nem toda plataforma implementa getLaunchUrl — o listener cobre */
        }
      } catch (e) {
        console.warn("[deeplink] listener nativo indisponível:", e?.message);
      }
    })();

    return () => {
      cancelado = true;
      if (remover) remover();
    };
  }, [navigate]);
}
